import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Input, Select, Button, Form, Space, Radio } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { AVAILABLE_ROUTES } from 'src/customizable-ui/aside/defaults.js';
import 'src/customizable-ui/aside/CustomizableAside.scss';

// Matches the slug used by CustomReportRoutes in
// src/reports/custom-report/CustomReportComponent.jsx so the nav link resolves.
const customReportLink = (label = '') =>
	`/custom-report/${label.toLowerCase()?.replaceAll(' ', '-')}`;

export const LinkEditorModal = ({
	visible,
	onSave,
	onCancel,
	initialData = {},
	customPageRoutes = [],
	allowCustomReport = false,
}) => {
	const [linkType, setLinkType] = useState('internal');
	const [selectedRoute, setSelectedRoute] = useState('');
	const [customUrl, setCustomUrl] = useState('');
	const [subPanels, setSubPanels] = useState([]);

	const allRoutes = useMemo(
		() =>
			[...AVAILABLE_ROUTES, ...customPageRoutes].sort((a, b) =>
				a.label.localeCompare(b.label)
			),
		[customPageRoutes]
	);

	useEffect(() => {
		if (visible && initialData) {
			const link = initialData.link || '';
			const isExternal =
				link.startsWith('http://') || link.startsWith('https://');

			setLinkType(isExternal ? 'external' : 'internal');
			if (isExternal) {
				setCustomUrl(link);
			} else {
				setSelectedRoute(link);
			}

			setSubPanels(initialData.panels || []);
		}
	}, [visible, initialData]);

	const handleAddSubPanel = () => {
		setSubPanels([
			...subPanels,
			{ title: '', link: '', linkType: 'internal', id: Date.now() },
		]);
	};

	const handleRemoveSubPanel = (index) => {
		setSubPanels(subPanels.filter((_, i) => i !== index));
	};

	const handleSubPanelChange = (index, field, value) => {
		const updated = [...subPanels];
		updated[index] = { ...updated[index], [field]: value };
		setSubPanels(updated);
	};

	const handleSave = () => {
		const mainLink = linkType === 'external' ? customUrl : selectedRoute;

		const processedPanels = subPanels
			.map((panel) => {
				if (panel.linkType === 'customReport') {
					const label = (panel.title || '').trim();
					return {
						title: panel.title,
						link: label ? customReportLink(label) : '',
						linkType: 'customReport',
						reportId: panel.reportId || '',
						reportType: panel.reportType || '',
						id: panel.id,
					};
				}
				return {
					title: panel.title,
					link: panel.linkType === 'external' ? panel.customUrl : panel.link,
					linkType: panel.linkType || 'internal',
					customUrl: panel.customUrl || '',
					id: panel.id,
				};
			})
			.filter((panel) =>
				panel.linkType === 'customReport'
					? panel.title && panel.link && panel.reportId && panel.reportType
					: panel.title && panel.link
			);

		onSave({
			link: mainLink,
			panels: processedPanels.length > 0 ? processedPanels : undefined,
		});
	};

	return (
		<Modal
			title="Edit Navigation Link"
			open={visible}
			onCancel={onCancel}
			onOk={handleSave}
			width={600}
			className="link-editor-modal"
		>
			<Form layout="vertical">
				<Form.Item label="Link Type">
					<Select
						value={linkType}
						onChange={setLinkType}
						style={{ width: '100%' }}
					>
						<Select.Option value="internal">Internal Route</Select.Option>
						<Select.Option value="external">External URL</Select.Option>
					</Select>
				</Form.Item>

				{linkType === 'internal' ? (
					<Form.Item label="Select Route">
						<Select
							showSearch
							value={selectedRoute}
							onChange={setSelectedRoute}
							placeholder="Choose a route"
							style={{ width: '100%' }}
							filterOption={(input, option) =>
								option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
						>
							{allRoutes.map((route) => (
								<Select.Option key={route.value} value={route.value}>
									{route.label}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				) : (
					<Form.Item label="Custom URL">
						<Input
							value={customUrl}
							onChange={(e) => setCustomUrl(e.target.value)}
							placeholder="https://example.com"
						/>
					</Form.Item>
				)}

				<div className="sub-panels-section">
					<div className="sub-panels-header">
						<h4>Sub-Panels</h4>
						<Button
							type="dashed"
							icon={<PlusOutlined />}
							onClick={handleAddSubPanel}
							size="small"
						>
							Add Sub-Panel
						</Button>
					</div>

					{subPanels.map((panel, index) => (
						<div key={panel.id || index} className="sub-panel-item">
							<Space direction="vertical" style={{ width: '100%' }}>
								<Input
									placeholder="Sub-panel title"
									value={panel.title}
									onChange={(e) =>
										handleSubPanelChange(index, 'title', e.target.value)
									}
								/>

								<Select
									value={panel.linkType || 'internal'}
									onChange={(value) =>
										handleSubPanelChange(index, 'linkType', value)
									}
									style={{ width: '100%' }}
									size="small"
								>
									<Select.Option value="internal">Internal Route</Select.Option>
									<Select.Option value="external">External URL</Select.Option>
									{allowCustomReport && (
										<Select.Option value="customReport">
											Custom Report
										</Select.Option>
									)}
								</Select>

								{panel.linkType === 'customReport' ? (
									<>
										<Input
											placeholder="Report ID"
											value={panel.reportId}
											onChange={(e) =>
												handleSubPanelChange(index, 'reportId', e.target.value)
											}
											size="small"
										/>
										<Radio.Group
											value={panel.reportType}
											onChange={(e) =>
												handleSubPanelChange(
													index,
													'reportType',
													e.target.value
												)
											}
										>
											<Radio value="dashboard">Dashboard</Radio>
											<Radio value="question">Question</Radio>
										</Radio.Group>
									</>
								) : (panel.linkType || 'internal') === 'internal' ? (
									<Select
										showSearch
										value={panel.link}
										onChange={(value) =>
											handleSubPanelChange(index, 'link', value)
										}
										placeholder="Choose a route"
										style={{ width: '100%' }}
										size="small"
										filterOption={(input, option) =>
											option.children
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
									>
										{allRoutes.map((route) => (
											<Select.Option key={route.value} value={route.value}>
												{route.label}
											</Select.Option>
										))}
									</Select>
								) : (
									<Input
										value={panel.customUrl}
										onChange={(e) =>
											handleSubPanelChange(index, 'customUrl', e.target.value)
										}
										placeholder="https://example.com"
										size="small"
									/>
								)}

								<Button
									type="text"
									danger
									icon={<DeleteOutlined />}
									onClick={() => handleRemoveSubPanel(index)}
									size="small"
								>
									Remove
								</Button>
							</Space>
						</div>
					))}
				</div>
			</Form>
		</Modal>
	);
};
